import React from 'react'
import { withFirebase } from '../../components/Fb'
import { Alert, Box, Button, Card, CardContent, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import MembershipSyncCard from '../../components/Config/MembershipSync/MembershipSyncCard'
import {
  CSV_SYNC,
  TENNISCLUB_SYNC,
  DEFAULT_SEPARATOR,
  MAX_SOURCES,
  pickCsvFields,
  pickTennisclubFields,
  validateCsvConfig,
  validateTennisclubConfig,
  translateBackendMessage,
  sourceLabel,
} from '../../components/utils/membershipsync'

const NAME_FIELD = {
  key: 'name',
  label: 'Nimi (valinnainen)',
  helper: 'Esimerkiksi Juniorit. Erottaa lähteet toisistaan tällä sivulla.',
}

const CSV_FIELDS = [
  NAME_FIELD,
  {
    key: 'url',
    label: 'CSV-tiedoston osoite',
    helper: 'Julkinen http(s)-osoite, josta tiedosto haetaan. Jos osoite vaihtuu toiselle palvelimelle, Cintoian asettamat tunnukset poistetaan.',
  },
  { key: 'separator', label: 'Erotinmerkki', helper: 'Sarakkeiden erotin, yleensä , tai ;', defaultValue: DEFAULT_SEPARATOR },
  { key: 'emailColumn', label: 'Sähköpostisarake', helper: 'Sarakkeen nimi otsikkorivillä täsmälleen samoin kirjoitettuna.' },
  {
    key: 'stateColumn',
    label: 'Tilasarake',
    helper: 'Arvo 1 (tai tyhjä) = voimassa oleva jäsen. Muu arvo päättää synkronoinnin antaman jäsenyyden.',
  },
  { key: 'membership', label: 'Annettava jäsenyys', type: 'membership' },
]

const TENNISCLUB_FIELDS = [
  NAME_FIELD,
  {
    key: 'tcclub',
    label: 'Seuran tunnus',
    helper: 'Seuran lyhenne tennisclub.fi-palvelussa. Jos seura vaihtuu, Cintoian asettamat tunnukset poistetaan.',
  },
  { key: 'membership', label: 'Annettava jäsenyys', type: 'membership' },
]

const SUPPORT_EMAIL = 'support@cintoia.com'

const CSV_EXAMPLE = ['Nimi;Email;Tila', 'Matti Meikäläinen;matti.meikalainen@example.com;1'].join('\n')

const TENNISCLUB_MODULE_INFO = (
  <>
    Tennisclub-synkronointi on lisämoduuli, jolla on kuukausimaksu. Ota yhteyttä{' '}
    <a href={'mailto:' + SUPPORT_EMAIL}>{SUPPORT_EMAIL}</a>, niin otamme sen käyttöön.
  </>
)

const SECTIONS = [
  {
    integration: CSV_SYNC,
    title: 'CSV-tuonti',
    description: 'Jäsenlista haetaan CSV-tiedostosta, jonka seura julkaisee verkko-osoitteessa. Lähteitä voi olla useita, esimerkiksi eri jäsenryhmille.',
    sourceName: 'CSV-lähde',
    addLabel: 'Lisää CSV-lähde',
    fields: CSV_FIELDS,
    validate: validateCsvConfig,
    pickFields: pickCsvFields,
    testable: true,
    credentialsClearedText:
      'Osoite on eri palvelimella kuin tunnukset, joten tunnukset poistettiin. Jos tiedosto vaatii kirjautumisen, pyydä Cintoiaa asettamaan tunnukset uudelleen.',
  },
  {
    integration: TENNISCLUB_SYNC,
    title: 'Tennisclub',
    description: 'Jäsenlista haetaan seuran tennisclub.fi-jäsenrekisteristä. Seuroja voi olla useita.',
    info: TENNISCLUB_MODULE_INFO,
    sourceName: 'Seura',
    addLabel: 'Lisää seura',
    fields: TENNISCLUB_FIELDS,
    validate: validateTennisclubConfig,
    pickFields: pickTennisclubFields,
    testable: false,
    notTestableText: 'Tennisclub-synkronointia ei voi testata hallintapaneelista. Tarkista tulos jäsenyyksistä seuraavan ajon jälkeen.',
    credentialsClearedText:
      'Seura vaihtui, joten Tennisclub-tunnukset poistettiin. Pyydä Cintoiaa asettamaan tunnukset uudelleen, muuten synkronointi ei toimi.',
  },
]

class ConfigMembershipSyncContainer extends React.Component {
  constructor(props) {
    super(props)
    // sources: { [integration]: [{ id, ...public config }] } from the backend.
    // drafts: { [integration]: [key] } new sources being filled in, not saved yet.
    this.state = { loading: true, loadError: null, sources: {}, drafts: {}, memberships: [] }
    this.draftSeq = 0
  }

  componentDidMount() {
    this.load()
  }

  load = async () => {
    const [configs, _memberships] = await Promise.all([
      this.props.firebase.getMembershipSyncs(),
      this.props.firebase.getMemberships(),
    ])
    const memberships = _memberships.docs
      .map((doc) => ({ ...doc.data(), id: doc.id }))
      .filter((m) => m.state !== 'deleted')
      .map((m) => ({ id: m.id, label: m.name || m.displayName || m.id }))
      .sort((a, b) => a.label.localeCompare(b.label))
    if (!configs || configs.error) {
      this.setState({
        loading: false,
        memberships,
        loadError: 'Synkronointiasetuksia ei saatu haettua: ' + translateBackendMessage(configs && configs.message),
      })
      return
    }
    const sources = {}
    SECTIONS.forEach(({ integration }) => {
      sources[integration] = Array.isArray(configs[integration]) ? configs[integration] : []
    })
    this.setState({ loading: false, loadError: null, memberships, sources })
  }

  addDraft = (integration) => {
    this.draftSeq += 1
    const drafts = this.state.drafts[integration] || []
    this.setState({ drafts: { ...this.state.drafts, [integration]: drafts.concat(['draft-' + this.draftSeq]) } })
  }

  dropDraft = (integration, key) => {
    const drafts = (this.state.drafts[integration] || []).filter((k) => k !== key)
    this.setState({ drafts: { ...this.state.drafts, [integration]: drafts } })
  }

  renderSection = (section) => {
    const saved = this.state.sources[section.integration] || []
    const drafts = this.state.drafts[section.integration] || []
    const common = {
      integration: section.integration,
      fields: section.fields,
      memberships: this.state.memberships,
      validate: section.validate,
      pickFields: section.pickFields,
      testable: section.testable,
      notTestableText: section.notTestableText,
      credentialsClearedText: section.credentialsClearedText,
    }
    const full = saved.length + drafts.length >= MAX_SOURCES
    return (
      <Card key={section.integration} sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant='h5' sx={{ mb: 1 }}>
            {section.title}
          </Typography>
          <Typography variant='caption' color='textSecondary' component='p' sx={{ mb: 2 }}>
            {section.description}
          </Typography>
          {section.info && (
            <Alert severity='info' sx={{ mb: 2 }}>
              {section.info}
            </Alert>
          )}
          {saved.length === 0 && drafts.length === 0 && (
            <Typography variant='body2' sx={{ mb: 2 }}>
              Ei käytössä. Lisää ensimmäinen lähde alla olevalla painikkeella.
            </Typography>
          )}
          {saved.map((source, i) => (
            <MembershipSyncCard
              key={source.id}
              {...common}
              sourceId={source.id}
              config={source}
              title={sourceLabel(source, i, section.sourceName)}
              onChanged={this.load}
            />
          ))}
          {drafts.map((key, i) => (
            <MembershipSyncCard
              key={key}
              {...common}
              sourceId={null}
              config={null}
              title={sourceLabel(null, saved.length + i, section.sourceName)}
              onCancel={() => this.dropDraft(section.integration, key)}
              onChanged={() => {
                this.dropDraft(section.integration, key)
                this.load()
              }}
            />
          ))}
          <Button
            variant='outlined'
            startIcon={<AddIcon />}
            disabled={full}
            onClick={() => this.addDraft(section.integration)}
          >
            {section.addLabel}
          </Button>
          {full && (
            <Typography variant='caption' color='textSecondary' component='p' sx={{ mt: 1 }}>
              Lähteitä voi olla enintään {MAX_SOURCES}.
            </Typography>
          )}
        </CardContent>
      </Card>
    )
  }

  renderDocumentation = () => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Typography variant='h5' sx={{ mb: 1 }}>
          Miten synkronointi toimii
        </Typography>
        <Typography variant='body2' component='div'>
          <p>
            Synkronointi antaa valitun jäsenyyden niille rekisteröityneille käyttäjille, joiden
            sähköpostiosoite löytyy ulkoisesta jäsenlistasta. Annettu jäsenyys on pysyvä
            (voimassa 31.12.2050 asti). Käyttäjä tunnistetaan sähköpostiosoitteesta, joten jäsen
            saa jäsenyyden vasta rekisteröidyttyään samalla osoitteella. Kullakin lähteellä on oma
            annettava jäsenyytensä, joten esimerkiksi aikuisille ja junioreille voi antaa eri jäsenyydet.
          </p>
          <p>
            <b>CSV-tuonti</b> hakee tiedostot annetuista osoitteista kahden tunnin välein päiväsaikaan.
            Tiedoston ensimmäinen rivi on otsikkorivi. Sarakkeiden nimet pitää kirjoittaa
            asetuksiin täsmälleen kuten otsikkorivillä (isot ja pienet kirjaimet erotellaan).
            Tilasarakkeen arvo 1 tai tyhjä tarkoittaa voimassa olevaa jäsentä. Lainausmerkeillä
            suojattuja kenttiä ei tueta, joten erotinmerkki ei saa esiintyä arvoissa. Testaa-painike
            hakee tiedoston ja kertoo, mitä synkronointi tekisi, tallentamatta mitään.
          </p>
          <p>Esimerkki CSV-tiedostosta (otsikkorivi ja yksi jäsen):</p>
          <Box
            component='pre'
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              bgcolor: 'action.hover',
              borderRadius: 1,
              p: 1.5,
              mb: 1,
              overflowX: 'auto',
            }}
          >
            {CSV_EXAMPLE}
          </Box>
          <p>
            Tähän tiedostoon sopivat asetukset: erotinmerkki <code>;</code>, sähköpostisarake{' '}
            <code>Email</code> ja tilasarake <code>Tila</code>. Arvo 1 tarkoittaa, että Matti on
            voimassa oleva jäsen; esimerkiksi arvo 0 päättäisi synkronoinnin antaman jäsenyyden.
          </p>
          <p>
            <b>Jäsenyyksien päättäminen (CSV-tuonti):</b> kun käyttäjän rivillä tilasarakkeen arvo on
            jokin muu kuin 1, synkronoinnin antama jäsenyys päätetään: se on voimassa vielä sen päivän
            loppuun. Vain synkronoinnin antamat jäsenyydet päätetään; käsin annettuihin jäsenyyksiin
            ei kosketa. Käyttäjää, joka puuttuu tiedostosta kokonaan, ei tulkita eronneeksi, joten
            hänen jäsenyytensä jää voimaan. Ennen tätä ominaisuutta annetut jäsenyydet Cintoia voi
            pyynnöstä ottaa synkronoinnin hallintaan, jotta nekin päättyvät.
          </p>
          <p>
            <b>Useita CSV-lähteitä:</b> jos saman jäsenyyden antaa useampi tiedosto, jäsenyys
            päätetään vasta, kun jäsen on merkitty ei-aktiiviseksi eikä mikään muu näistä
            tiedostoista merkitse häntä aktiiviseksi. Jos jokin saman jäsenyyden tiedosto ei ole
            haettavissa, sen jäsenyyksiä ei sillä kerralla päätetä lainkaan.
          </p>
          <p>
            <b>Tennisclub</b> hakee kunkin seuran jäsenet tennisclub.fi-palvelusta kahden tunnin välein
            päiväsaikaan. Mukaan otetaan henkilöt, joilla on jäsenyys seurassa. Tennisclub-tunnukset
            asettaa Cintoia. Tätä synkronointia ei voi testata hallintapaneelista. Tennisclub vain
            antaa jäsenyyksiä eikä päätä niitä, koska jäsenen puuttuminen tennisclub.fi:n tiedoista ei
            aina tarkoita jäsenyyden loppumista. Tennisclub-synkronointi on lisämoduuli, jolla on
            kuukausimaksu. Ota yhteyttä{' '}
            <a href={'mailto:' + SUPPORT_EMAIL}>{SUPPORT_EMAIL}</a>, niin otamme sen käyttöön.
          </p>
          <p>
            <b>Tunnukset:</b> jos tiedosto tai jäsenrekisteri vaatii kirjautumisen, Cintoia asettaa
            tunnukset lähdekohtaisesti. Ne ovat voimassa vain sille palvelimelle tai seuralle, jolle ne
            on asetettu. Jos osoite siirtyy toiselle palvelimelle tai seura vaihtuu, tunnukset
            poistetaan ja Cintoian pitää asettaa ne uudelleen. Uudelle lähteelle Cintoia asettaa
            tunnukset erikseen.
          </p>
          <p>
            Lähteen poistaminen ei poista eikä päätä jo annettuja jäsenyyksiä.
          </p>
        </Typography>
      </CardContent>
    </Card>
  )

  render() {
    if (this.state.loading) return <div className='content' />
    const { loadError } = this.state
    return (
      <div className='content'>
        <Box sx={{ maxWidth: 960 }}>
          {loadError ? (
            <Alert severity='error' sx={{ mb: 2 }}>
              {loadError}
            </Alert>
          ) : (
            SECTIONS.map(this.renderSection)
          )}
          {this.renderDocumentation()}
        </Box>
      </div>
    )
  }
}

export default withFirebase(ConfigMembershipSyncContainer)
